import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { translateError } from "@/i18n/errors";
import { useI18n } from "@/i18n/I18nContext";

const Stat = ({ label, value, testId }) => (
  <div className="min-w-0 rounded-xl border border-neutral-800 bg-black/40 p-3">
    <dt className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</dt>
    <dd className="mt-1 font-display text-lg font-bold text-[var(--gold-bright)]" data-testid={testId}><bdi>{value}</bdi></dd>
  </div>
);

const Field = ({ label, value, onChange, testId, step = 1 }) => (
  <label className="min-w-0 text-xs text-neutral-400">{label}
    <input type="number" min={1} step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 block w-full rounded-lg border border-neutral-700 bg-black/50 px-3 py-2 text-sm text-white" />
  </label>
);

export const DownloadRegulatorCard = () => {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/admin/dl-regulator").then(({ data }) => {
    setS(data);
    setForm((f) => f || { min_slots: data.min_slots, max_slots: data.max_slots, total_mbps: data.total_mbps });
  }).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/admin/dl-regulator", { min_slots: Number(form.min_slots), max_slots: Number(form.max_slots), total_mbps: Number(form.total_mbps) });
      setS(data);
      toast.success(t("reg.saved"));
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  if (!s || !form) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-4 sm:p-6" data-testid="dl-regulator-card">
      <h2 className="flex items-center gap-2 font-display text-base font-bold uppercase tracking-wider text-[var(--gold)] md:text-lg">
        <Gauge className="h-5 w-5 shrink-0" /> {t("reg.title")}
      </h2>
      <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t("reg.active")} value={`${s.active} / ${s.slots}`} testId="reg-active" />
        <Stat label={t("reg.waiting")} value={s.waiting} testId="reg-waiting" />
        <Stat label={t("reg.slots")} value={`${s.min_slots}–${s.max_slots}`} testId="reg-slots" />
        <Stat label={t("reg.throughput")} value={`${s.throughput_mbps} Mbit/s`} testId="reg-throughput" />
      </dl>
      <form onSubmit={save} className="mt-4 grid gap-3 sm:grid-cols-4 sm:items-end" data-testid="reg-form">
        <Field label={t("reg.min")} value={form.min_slots} onChange={set("min_slots")} testId="reg-min" />
        <Field label={t("reg.max")} value={form.max_slots} onChange={set("max_slots")} testId="reg-max" />
        <Field label={t("reg.mbps")} value={form.total_mbps} onChange={set("total_mbps")} step={0.5} testId="reg-mbps" />
        <button type="submit" className="btn-gold btn-sm justify-center" data-testid="reg-save">{t("reg.save")}</button>
      </form>
      <h3 className="mt-5 text-xs font-bold uppercase tracking-wider text-neutral-300">{t("reg.decisions")}</h3>
      <ul className="mt-2 space-y-1 text-xs text-neutral-300" data-testid="reg-decisions">
        {s.decisions.length === 0 && <li className="text-neutral-500">{t("reg.none")}</li>}
        {s.decisions.slice(0, 5).map((d) => (
          <li key={d.at + d.action} className="flex flex-wrap gap-x-2">
            <bdi className="text-neutral-500">{new Date(d.at).toLocaleTimeString()}</bdi>
            <span>{t(d.action === "up" ? "reg.up" : "reg.down")}</span>
            <bdi className="text-neutral-500">→ {d.slots} · CPU {d.cpu} % · RAM {d.mem} %</bdi>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-neutral-500" data-testid="reg-offload">{t("reg.offload")}</p>
    </section>
  );
};
